import React from "react";
import { View, Text, TouchableOpacity, Image } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppColors } from "../../utility/AppColors";
import { AppImages } from "../../utility/AppImages";

interface Player {
  userID: string;
  profilePicture: string;
}

interface PlayerSelectionModalProps {
  visible: boolean;
  onClose: () => void;
  onCancel?: () => void;
  organizer: Player | null;
  challenger: Player | null;
  onSelectPlayer: (playerId: string) => void;
  selectedPlayerId: string | null;
  position?: { top?: number; left?: number; right?: number; bottom?: number };
}

const PlayerSelectionModal: React.FC<PlayerSelectionModalProps> = ({
  visible,
  onClose,
  onCancel,
  organizer,
  challenger,
  onSelectPlayer,
  selectedPlayerId,
  position = {},
}) => {
  if (!visible) return null;

  return (
    <View style={{ position: "relative" }}>
      <LinearGradient
        colors={["#1F2937", "#111827"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{
          ...position,
          display: "flex",
          padding: 16,
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 16,
          borderRadius: 12,
          borderWidth: 0.8,
          borderColor: "#374151",
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.3,
          shadowRadius: 8,
          elevation: 8,
        }}
      >
        {/* Header */}
        <View style={{ width: "100%" }}>
          <Text
            style={[
              AppFontStyle.SEMIBOLD_ANYBODY_12,
              { color: AppColors.white, textAlign: "center" },
            ]}
          >
            Choose player you want to send
          </Text>
        </View>

        {/* Players - 50% each */}
        <View
          style={{
            flexDirection: "row",
            gap: 12,
            alignItems: "center",
            width: "100%",
          }}
        >
          {/* Organizer - 50% */}
          <TouchableOpacity
            onPress={() => onSelectPlayer(organizer?.userID || "")}
            style={{
              flex: 1,
              display: "flex",
              padding: 12,
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 8,
              borderRadius: 8,
              borderWidth: 1,
              borderColor:
                selectedPlayerId === organizer?.userID
                  ? "#DC2626"
                  : "transparent",
              backgroundColor:
                selectedPlayerId === organizer?.userID
                  ? "rgba(220, 38, 38, 0.10)"
                  : "transparent",
              cursor: "pointer",
            }}
          >
            <View
              style={{
                width: 60,
                height: 60,
                borderRadius: 30,
                overflow: "hidden",
                backgroundColor: AppColors.white,
              }}
            >
              <Image
                source={{
                  uri: organizer?.profilePicture || AppImages.icProfile,
                }}
                style={{
                  width: 60,
                  height: 60,
                }}
                resizeMode="cover"
              />
            </View>
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_12,
                { color: AppColors.white, textAlign: "center" },
              ]}
              numberOfLines={1}
            >
              {organizer?.userID || "Player 1"}
            </Text>
          </TouchableOpacity>

          {/* Challenger - 50% */}
          <TouchableOpacity
            onPress={() => onSelectPlayer(challenger?.userID || "")}
            style={{
              flex: 1,
              display: "flex",
              padding: 12,
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 8,
              borderRadius: 8,
              borderWidth: 1,
              borderColor:
                selectedPlayerId === challenger?.userID
                  ? "#DC2626"
                  : "transparent",
              backgroundColor:
                selectedPlayerId === challenger?.userID
                  ? "rgba(220, 38, 38, 0.10)"
                  : "transparent",
              cursor: "pointer",
            }}
          >
            <View
              style={{
                width: 60,
                height: 60,
                borderRadius: 30,
                overflow: "hidden",
                backgroundColor: AppColors.white,
              }}
            >
              <Image
                source={{
                  uri: challenger?.profilePicture || AppImages.icProfile,
                }}
                style={{
                  width: 60,
                  height: 60,
                }}
                resizeMode="cover"
              />
            </View>
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_12,
                { color: AppColors.white, textAlign: "center" },
              ]}
              numberOfLines={1}
            >
              {challenger?.userID || "Player 2"}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Send Button */}
        <TouchableOpacity
          onPress={onClose}
          disabled={!selectedPlayerId}
          style={{
            opacity: selectedPlayerId ? 1 : 0.5,
            cursor: "pointer",
          }}
        >
          <LinearGradient
            colors={
              selectedPlayerId
                ? ["#DC2626", "#991B1B"]
                : [AppColors.gray_9499A4, AppColors.gray_9499A4]
            }
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={{
              display: "flex",
              width: 120,
              height: 44,
              paddingHorizontal: 32,
              paddingVertical: 12,
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 6,
            }}
          >
            <Text
              style={[AppFontStyle.BOLD_ANYBODY_12, { color: AppColors.white }]}
            >
              Send
            </Text>
          </LinearGradient>
        </TouchableOpacity>
      </LinearGradient>

      {/* Close Button (X) - Top Right */}
      <TouchableOpacity
        onPress={() => {
          if (onCancel) {
            onCancel();
          }
        }}
        style={{
          position: "absolute",
          top: -10,
          right: -10,
          backgroundColor: "#DC2626",
          borderRadius: 15,
          width: 30,
          height: 30,
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.3,
          shadowRadius: 4,
          elevation: 5,
        }}
      >
        <Image
          source={AppImages.icClose}
          style={{ width: 12, height: 12, tintColor: AppColors.white }}
          resizeMode="contain"
        />
      </TouchableOpacity>
    </View>
  );
};

export default PlayerSelectionModal;
